import React, {FC, useEffect, useState} from "react"
import {debounce, uniqBy} from "lodash"

import Select, {SelectProps, SelectOption} from "@irati/ui/src/FormItem/Select"
import {ALL_TEAMS} from "@irati-web/admin/src/components/Reporting/Region"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {useLazyTeamsPaginated} from "@irati/identity/hooks"
import {useIntl} from "@irati/utils/lang"
import teamsService from "@irati/identity/services/teams"

interface Option {
  value?: string
  label?: string
}

interface Props extends SelectProps {
  includeAllTeams?: boolean
  teamIds?: string[]
  returnSelectedOption?: boolean
  withBranding?: boolean
}

const TeamSearch: FC<Props> = ({
  onChange,
  isDisabled,
  value,
  isCreatable,
  includeAllTeams = true,
  teamIds,
  returnSelectedOption,
  withBranding,
  options: includeOptions,
  ...selectProps
}) => {
  const {formatMessage} = useIntl()
  const [teamName, setTeamName] = useState("")
  const [cursor, setCursor] = useState(null)
  const [searchCursor, setSearchCursor] = useState(null)
  const [searchAfter, setSearchAfter] = useState(null)
  const [hasBranding, setHasBranding] = useState(withBranding)
  const [options, setOptions] = useState<SelectOption[]>([])
  const [selectedValue, setSelectedValue] = useState(value || (includeAllTeams ? ALL_TEAMS : undefined))
  const [isLoading, setIsLoading] = useState(true)
  const {business} = useAuth()
  const [teamsPaginated, apollo] = useLazyTeamsPaginated({
    businessId: business.uuid,
    teamName,
    searchAfter,
    hasBranding
  })

  useEffect(() => {
    teamsPaginated()
  }, [])

  useEffect(() => {
    if (includeAllTeams && options.length === 0) {
      setOptions([{label: formatMessage({id: "platform.allTeams"}), value: ALL_TEAMS}])
    }
    if (includeOptions && options.length === 0) {
      setOptions(includeOptions)
    }
  }, [includeAllTeams, includeOptions])

  useEffect(() => {
    if (withBranding !== hasBranding) {
      setHasBranding(withBranding)
    }
  }, [withBranding])

  useEffect(() => {
    let updatedOptions: Option[] = []
    if (apollo.data) {
      console.log("data be", apollo.data)
      const baseTeamOptions = apollo.data.teams.map((team) => {
        return {label: team.teamName, value: team.uuid}
      })
      if (teamIds && teamIds.length > 0) {
        const filteredOptionsArray = baseTeamOptions.filter((el) => teamIds.indexOf(el.value) < 0)
        updatedOptions = uniqBy([...options, ...filteredOptionsArray], "value")
      } else {
        updatedOptions = uniqBy([...options, ...baseTeamOptions], "value")
      }

      if (apollo.data?.searchAfter) {
        if (teamName) {
          setSearchCursor(apollo.data.searchAfter)
        } else {
          setCursor(apollo.data.searchAfter)
        }
      } else {
        if (teamName) {
          setSearchCursor(null)
        } else {
          setCursor(null)
        }
      }
      if (Array.isArray(selectedValue)) {
        if (!selectedValue.every((val) => options.some((option) => option.value === val))) {
          value.map((teamId) => {
            teamsService.get({tenantId: business.uuid, teamId}).then((team) => {
              const teamOption = {label: team.teamName, value: team.uuid}
              updatedOptions = [teamOption, ...updatedOptions]
              updateOptions(updatedOptions)
            })
          })
        } else {
          updateOptions(updatedOptions)
        }
      } else if (selectedValue && selectedValue !== ALL_TEAMS) {
        if (!options.some((option) => option.value === selectedValue)) {
          teamsService
            .get({tenantId: business.uuid, teamId: selectedValue})
            .then((team) => {
              const teamOption = {label: team.teamName, value: team.uuid}
              updatedOptions = [teamOption, ...updatedOptions]
              updateOptions(updatedOptions)
            })
            .catch(() => {
              updateOptions(updatedOptions)
            })
        } else {
          updateOptions(updatedOptions)
        }
      } else {
        updateOptions(updatedOptions)
      }
    }
  }, [apollo?.data])

  useEffect(() => {
    if (value !== selectedValue) {
      setSelectedValue(value)
    }
  }, [value])

  const updateOptions = (updatedOptions) => {
    setOptions(updatedOptions)
    setIsLoading(false)
  }

  const getNextPage = () => {
    const nextCursor = teamName ? searchCursor : cursor
    setSearchAfter(nextCursor)
  }

  const updateInput = debounce((inputValue) => {
    setTeamName(inputValue)
  }, 1000)

  const handleInputChange = (inputValue, {action}) => {
    if (action === "input-change") {
      setIsLoading(true)
      updateInput.cancel()
      updateInput(inputValue)
    } else if (action === "set-value" || action === "input-blur") {
      updateInput.cancel()
    }
  }

  const handleChange = (updatedValue: string) => {
    updateInput.cancel()
    if (onChange) {
      if (returnSelectedOption) {
        const selectOption = options.find((op) => op.value === updatedValue)
        return onChange(selectOption)
      }
      onChange(updatedValue)
    }
  }

  return (
    <Select
      value={selectedValue}
      options={options}
      onInputChange={handleInputChange}
      onMenuScrollToBottom={getNextPage}
      isLoading={isCreatable ? undefined : isLoading}
      onChange={handleChange}
      autoComplete={false}
      aria-label="Team Search"
      isDisabled={isDisabled}
      isCreatable={isCreatable}
      {...selectProps}
    />
  )
}

export default TeamSearch
